import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Field, FormError, Input } from "@/components/ui/field";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Empty, PageHeader, Rows } from "@/features/admin/parts";
import type { Schemas } from "@/lib/api/client";
import {
  configRequestsQuery,
  useApproveConfigRequest,
  useRejectConfigRequest,
} from "@/lib/api/queries";
import { formatRelative } from "@/lib/format";

const VIEWS = { pending: "Ждут ответа", all: "Все" } as const;
type View = keyof typeof VIEWS;

export const Route = createFileRoute("/_app/admin/requests")({
  validateSearch: z.object({ view: z.enum(["pending", "all"]).optional() }),
  loaderDeps: ({ search }) => ({ view: search.view ?? "pending" }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(
      configRequestsQuery(deps.view === "pending" ? "pending" : null),
    ),
  component: RequestsPage,
});

type Request = Schemas["AdminConfigRequestOut"];

const STATUS: Record<Request["status"], { label: string; tone: string }> = {
  pending: { label: "ждёт ответа", tone: "text-amber-text" },
  approved: { label: "одобрен", tone: "text-online" },
  rejected: { label: "отклонён", tone: "text-muted" },
};

function RequestsPage() {
  const view: View = Route.useSearch().view ?? "pending";
  const navigate = Route.useNavigate();
  const { data } = useSuspenseQuery(configRequestsQuery(view === "pending" ? "pending" : null));

  return (
    <>
      <PageHeader title="Запросы конфигов" />
      <p className="mt-2 text-sm text-muted">
        Одобрение поднимает личный лимит пользователя на один. Конфиг он создаёт сам.
      </p>
      <Tabs
        className="mt-6"
        value={view}
        onValueChange={(next) =>
          navigate({ search: next === "pending" ? {} : { view: next as View } })
        }
      >
        <TabsList className="inline-flex">
          {Object.entries(VIEWS).map(([value, label]) => (
            <TabsTrigger key={value} value={value}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="mt-4">
        {data.length === 0 ? (
          <Empty>{view === "pending" ? "Новых запросов нет." : "Запросов ещё не было."}</Empty>
        ) : (
          <Rows>
            {data.map((request) => (
              <RequestRow key={request.id} request={request} />
            ))}
          </Rows>
        )}
      </div>
    </>
  );
}

function RequestRow({ request }: { request: Request }) {
  const approve = useApproveConfigRequest();
  const [rejecting, setRejecting] = useState(false);
  const status = STATUS[request.status];

  return (
    <li className="grid gap-2 px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-4">
      <div className="min-w-0">
        <p>
          <Link
            to="/admin/users/$userId"
            params={{ userId: request.user_id }}
            className="underline-offset-4 hover:underline"
          >
            {request.username}
          </Link>
          <span className="text-sm text-muted">
            {" "}
            · своих {request.self_configs_used} из {request.self_config_limit} ·{" "}
            {formatRelative(request.created_at)}
          </span>
        </p>
        {request.comment && <p className="mt-0.5 text-sm">«{request.comment}»</p>}
        {request.status !== "pending" && (
          <p className="mt-0.5 text-sm">
            <span className={status.tone}>{status.label}</span>
            {request.admin_comment && (
              <span className="text-muted"> — {request.admin_comment}</span>
            )}
          </p>
        )}
      </div>
      {request.status === "pending" && (
        <div className="flex gap-2">
          <Button
            size="sm"
            pending={approve.isPending}
            onClick={() =>
              approve.mutate(request.id, {
                onSuccess: () =>
                  toast.success(
                    `Лимит ${request.username} поднят до ${request.self_config_limit + 1}`,
                  ),
                onError: (error) => toast.error(error.message),
              })
            }
          >
            Одобрить
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRejecting(true)}>
            Отклонить
          </Button>
        </div>
      )}
      <RejectDialog request={request} open={rejecting} onOpenChange={setRejecting} />
    </li>
  );
}

function RejectDialog({
  request,
  open,
  onOpenChange,
}: {
  request: Request;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const reject = useRejectConfigRequest();
  const [comment, setComment] = useState("");

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Отклонить запрос ${request.username}?`}
      description="Пользователь увидит отказ и ваш комментарий на главной. Попросить снова он сможет."
    >
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          reject.mutate(
            { id: request.id, comment: comment.trim() || null },
            {
              onSuccess: () => {
                toast.success("Запрос отклонён");
                onOpenChange(false);
              },
            },
          );
        }}
      >
        <Field label="Комментарий" hint="Необязательно">
          {(control) => (
            <Input
              {...control}
              value={comment}
              maxLength={300}
              onChange={(e) => setComment(e.target.value)}
            />
          )}
        </Field>
        <FormError message={reject.error?.message} />
        <Button type="submit" variant="danger" pending={reject.isPending}>
          Отклонить
        </Button>
      </form>
    </Dialog>
  );
}
